import { createContext, useContext, useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock } from "lucide-react";
import { api, LOGO_URL } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";

const SiteContext = createContext({ settings: null, refresh: () => {} });
export const useSiteSettings = () => useContext(SiteContext);

export function SiteSettingsProvider({ children }) {
  const [settings, setSettings] = useState(null);
  const refresh = useCallback(() => api.get("/settings").then((r) => setSettings(r.data)).catch(() => setSettings({ site_public: true })), []);
  useEffect(() => { refresh(); }, [refresh]);
  return <SiteContext.Provider value={{ settings, refresh }}>{children}</SiteContext.Provider>;
}

export function ComingSoon({ message }) {
  return (
    <div className="min-h-screen bg-black text-white flex flex-col" data-testid="coming-soon-page">
      <div className="flex-1 flex items-center justify-center px-6">
        <motion.div initial={{ opacity: 0, y: 14 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.6 }} className="max-w-xl w-full text-center">
          <img src={LOGO_URL} alt="DCEPtNZ" className="w-40 h-40 sm:w-52 sm:h-52 mx-auto rounded-xl mb-10" />
          <div className="font-mono text-[11px] uppercase tracking-[0.2em] text-white/50 mb-4">Data Centre Exposure Project NZ</div>
          <h1 className="font-serif text-4xl sm:text-5xl tracking-tight mb-5">Coming soon</h1>
          <p className="text-white/70 text-base sm:text-lg leading-relaxed" data-testid="coming-soon-message">
            {message || "The DCEPtNZ research archive is being prepared and will open to the public shortly."}
          </p>
        </motion.div>
      </div>
      <div className="px-6 py-6 flex items-center justify-between text-xs text-white/40">
        <span>© {new Date().getFullYear()} DCEPtNZ</span>
        <Link to="/login" className="inline-flex items-center gap-1.5 hover:text-white/80 transition-colors" data-testid="coming-soon-admin-link"><Lock size={12} /> Administrator sign in</Link>
      </div>
    </div>
  );
}

// Wraps public routes: shows Coming Soon unless the site is public or an admin is signed in.
export default function SiteGate({ children }) {
  const { settings } = useSiteSettings();
  const { user, loading } = useAuth();
  if (!settings || loading) return <div className="min-h-screen bg-white" />;
  if (!settings.site_public && !user) return <ComingSoon message={settings.coming_soon_message} />;
  return children;
}
